import { useCallback, useEffect, useMemo, useState } from 'react'
import dayjs from 'dayjs'

import Text from '@/elements/Text'
import Flex from '@/elements/Flex'
import Loading from '@/elements/Loading'
import ImageGrid from '@/components/ImageGrid'
import FormFile from '@/elements/FormFile'
import {
  useGetIndividualSearchAssetsRefinement,
  updateIndividualSearchAssetsRefinement,
  deleteIndividualSearchAssetsRefinement
} from '@/api/organizationIndividual'
import type { SubjectResponse } from '@/api/types/Subject'
import toast from '@/utils/toast'
import type { IndividualImageSearchRefinementResponse } from '@/api/types/Organization'
import { checkImageStatus } from '@/utils/individualHeadshots'
import { MAX_HEADSHOTS_PER_INDIVIDUAL } from '@/constants'

function IndividualAdditionalImages({ individualId }: { individualId: SubjectResponse['id'] }) {
  const [uploadingNewImages, setUploadingNewImages] = useState(false)
  const { data, refetch, isLoading, isFetching } = useGetIndividualSearchAssetsRefinement(individualId)
  const additionalImages = useMemo(() => {
    return data?.data || []
  }, [data])
  const alreadyHasImages = additionalImages.length > 0
  const hasImagesPending = additionalImages.some(image => checkImageStatus(image).isWaiting)
  const initialLoading = isLoading || hasImagesPending

  useEffect(() => {
    if (isFetching || !hasImagesPending) return () => null

    const timer = setTimeout(() => {
      refetch()
    }, 2000)
    return () => clearTimeout(timer)
  }, [additionalImages, hasImagesPending, isFetching, refetch])

  const submitNewImages = useCallback(async (files: FileList) => {
    if (!individualId) return
    const formData = new FormData()
    Array.from(files).forEach(file => {
      formData.append('images', file)
    })
    setUploadingNewImages(true)
    try {
      await updateIndividualSearchAssetsRefinement(individualId, formData)
      await refetch()
      toast.success('Additional images updated successfully!')
    } catch {
      toast.error('Failed to update additional images')
    } finally {
      setUploadingNewImages(false)
    }
  }, [individualId, refetch])

  const submitDeleteImages = useCallback(async (externalId: string) => {
    if (!individualId) return
    const imageToDelete = additionalImages.find(image => image.externalId === externalId)
    if (!imageToDelete) {
      toast.error('Failed to delete image: missing external ID')
      return
    }
    try {
      await deleteIndividualSearchAssetsRefinement(individualId, { externalIds: [externalId] })
      refetch()
      toast.success('Additional image deleted successfully!')
    } catch {
      toast.error('Failed to delete additional image')
    }
  }, [individualId, additionalImages, refetch])

  const additionalImagesAsFiles = useMemo(() => {
    return additionalImages.map((image: IndividualImageSearchRefinementResponse) => {
      const file = new File([], image.metadata.fileName, {
        type: image.metadata.mimeType,
        lastModified: dayjs(image.createdAt).valueOf()
      })
      Object.defineProperty(file, 'url', {
        value: image.url,
        writable: false
      })
      return file
    })
  }, [additionalImages])


  return (
    <Flex wfull align='end' gap={6}>
      <Flex align='center'>
        <FormFile
          name='additionalImages'
          label=''
          accept='.png, .jpg, .jpeg'
          placeholder={alreadyHasImages ? 'Upload Images' : 'Upload Initial Image'}
          triggerVariant='primary'
          files={additionalImagesAsFiles}
          onChange={e => {
            const files = e.target.files
            if (files) submitNewImages(files)
          }}
          disabled={additionalImages.length >= MAX_HEADSHOTS_PER_INDIVIDUAL}
          maxFiles={MAX_HEADSHOTS_PER_INDIVIDUAL}
          maxFileSize={8}
          minImageWidth={600}
          minImageHeight={600}
          multiple={alreadyHasImages}
          preview='default'
          preventDuplicate
        />
        {!alreadyHasImages ? null : (
          <Text color='black-light-40'>{`${additionalImages.length}/${MAX_HEADSHOTS_PER_INDIVIDUAL} Uploaded`}</Text>
        )}
      </Flex>
      {uploadingNewImages || initialLoading ? (
        <Flex align='center' justify='center' wfull py={4}>
          <Loading />
        </Flex>
      ) : (
        <ImageGrid images={additionalImages} onImageRemove={submitDeleteImages} />
      )}
    </Flex>
  )
}

export default IndividualAdditionalImages
